import { ReplayOutlined } from "@mui/icons-material";
import {
    Alert,
    Box,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    TablePagination,
    useTheme,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../Utilities/AppContext";
import { GET, POST } from "../../../Utilities/BaseService";
import { Print } from "../../../Utilities/Print";
import { BillFilter } from "./BillFilter";
import { SingleBill } from "./SingleBill";

const fetchBills = async (filter: any) => {
    return await GET("/reading/bill/generate", filter);
};

export type Filter = {
    district: number | string;
    village: number | string;
    hamlet: number | string;
    powerstation: number | string;
    id: number | string;
    month: string;
    year: string;
    meterNo: (string | number)[];
    search: string;
};

export const Bills = () => {
    const printRef = useRef<any>();
    const [printMode, setPrintMode] = useState(false);
    const [pagination, setPagination] = useState({
        page: 0,
        count: 0,
        limit: 100,
    });
    const [count, setCount] = useState(0);
    const [filter, setFilter] = useState<Filter>({
        district: "all",
        village: "all",
        hamlet: "all",
        powerstation: "all",
        id: "",
        month: "",
        year: "",
        meterNo: [],
        search: "",
    });
    const [ids, setIds] = useState<(string | number)[]>([]);
    const [showBills, setShowBills] = useState(false);
    const [feedback, setFeedBack] = useState({
        show: false,
        message: "",
        severity: "",
    });

    const { store, setStore, user } = useContext(AppContext);

    const qc = useQueryClient();
    const theme = useTheme();

    const { data: billsResponse, fetchStatus: billStatus } = useQuery(
        ["bills", showBills, filter, pagination.page, pagination.limit, count],
        () =>
            fetchBills({
                ...Object.fromEntries(
                    Object.entries(filter).filter((x) => x[1] && x[1] !== "all")
                ),
                page: pagination.page + 1,
                search: undefined,
                meterNo: filter.search ? [filter.search] : undefined,
                limit: pagination.limit,
                status: "active",
                ignoreCompleted: true,
            }),
        {
            enabled: showBills,
            onSuccess(res) {
                setPagination({ ...pagination, count: res.data.count });
                if (billsResponse?.data.rows.length > 0) {
                    const bills = billsResponse?.data.rows;
                }

                setFeedBack({
                    severity: "",
                    message: "",
                    show: false,
                });
            },
            onError(err: any) {
                setFeedBack({
                    severity: "error",
                    message: err.response.data.message,
                    show: true,
                });
            },
        }
    );

    useEffect(() => {
        if (filter.meterNo.length) {
            qc.invalidateQueries(["bills", ids, showBills]);
        } else setIds([]);
    }, [filter]);

    const handlePrint = async () => {
        console.log(user, "User")
        const printData =
            billsResponse?.data.rows.map((bill: Bill) => ({
                meterId: bill.meter?.id,
                month: filter.month ? filter.month : bill.month ? bill.month : undefined,
                year: filter.year ? filter.year : bill.year ? bill.year : undefined,
                operator: user.id
            })) || [];


        await POST("/reading/print-bill-history", {
            bills: printData,
        });

        setPrintMode(true);
    };

    return (
        <Grid container spacing={1}>
            <BillFilter
                filter={filter}
                setFilter={setFilter}
                isLoading={false}
                setShowBills={setShowBills}
            />

            {/* <Grid item xs={12}>
                <FormControl>
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                            <FormLabel>Group bills by:</FormLabel>
                        </Grid>

                        <Grid item>
                            <RadioGroup>
                                <Grid container>
                                    <Grid item>
                                        <FormControlLabel
                                            label="Village"
                                            onChange={() =>
                                                setGroupBillsBy("village")
                                            }
                                            control={
                                                <Radio
                                                    checked={
                                                        groupBillsBy ===
                                                        "village"
                                                    }
                                                    value="village"
                                                />
                                            }
                                        />
                                    </Grid>

                                    <Grid item>
                                        <FormControlLabel
                                            label="Power Station"
                                            onChange={() =>
                                                setGroupBillsBy("powerstation")
                                            }
                                            control={
                                                <Radio
                                                    checked={
                                                        groupBillsBy ===
                                                        "powerstation"
                                                    }
                                                    value="powerstation"
                                                />
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </RadioGroup>
                        </Grid>
                    </Grid>
                </FormControl>
            </Grid> */}

            <Grid
                item
                xs={12}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box display="flex" alignItems="center" gap={theme.spacing(1)}>
                    <Print
                        componentRef={printRef}
                        setPrintMode={setPrintMode}
                        onBeforePrint={handlePrint}
                    />

                    <IconButton
                        size="small"
                        title="Reload View"
                        onClick={() => setCount(count + 1)}
                    >
                        <ReplayOutlined fontSize="small" />
                    </IconButton>
                </Box>

                {/*    <BlindPagination
                    pagination={pagination}
                    setPagination={setPagination}
                /> */}

                <TablePagination
                    component="div"
                    count={pagination.count}
                    page={pagination.page}
                    onPageChange={(e, newPage) =>
                        setPagination({ ...pagination, page: newPage })
                    }
                    rowsPerPage={pagination.limit}
                    onRowsPerPageChange={(e) =>
                        setPagination({
                            ...pagination,
                            limit: parseInt(e.target.value),
                        })
                    }
                    rowsPerPageOptions={[10, 50, 100, 250]}
                />
            </Grid>

            <Grid item xs={12}>
                <Divider flexItem />
            </Grid>

            <Grid item xs={12}>
                {billStatus === "fetching" && <LinearProgress />}
            </Grid>

            <Grid item xs={12}>
                {billsResponse?.data.rows.length === 0 && (
                    <Alert severity="info">No bills found</Alert>
                )}
            </Grid>

            <Grid item xs={12}>
                {feedback.show && (
                    <Alert severity={feedback.severity as any}>
                        {feedback.message}
                    </Alert>
                )}
            </Grid>

            <Grid
                item
                container
                xs={12}
                component={Paper}
                ref={printRef}
                elevation={0}
                sx={(theme) => ({
                    "@media print": {
                        "& > *": {
                            pageBreakAfter: "always",
                            pageBreakInside: "avoid",
                            margin: 4,
                        },
                        "&:last-child > *": {
                            pageBreakAfter: "auto",
                        },
                    },
                })}
            >
                {showBills &&
                    billsResponse?.data.rows.map((bill: Bill) => (
                        <SingleBill
                            key={bill.id}
                            _bill={bill}
                            gridProps={{
                                mb: 5,
                            }}
                        />
                    ))}
            </Grid>
        </Grid>
    );
};
